import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import useDataset from "datasets/hooks/useDataset";
import usePermissions from "datasets/hooks/usePermissions";
import useDatasetDashboards from "datasets/pages/dashboards/hooks/useDatasetDashboards";
import useReport from "datasets/pages/dashboards/hooks/useReport";
import queryString from "query-string";
import yn from "yn";
import Loading from "~/components/Loading";
import SigmaEmbed from "~/components/SigmaEmbed";
import useErrorToast from "~/contexts/error-toast";
import rumClient, { trackEvent } from "~/utils/monitoring";

const { VITE_CATALOG_API } = import.meta.env;

const DatasetReport = () => {
  const { datasetId, reportId } = useParams();
  const { data: report } = useReport();
  const { mutate: mutateDataset } = useDataset();
  const { data: dashboards, mutate: mutateDashboards } = useDatasetDashboards();
  const { showError } = useErrorToast();
  const navigate = useNavigate();
  const hasEditPermission = usePermissions("edit:catalog-report");

  const handleClose = () => navigate("..", { relative: "path" });

  const handleRemove = async () => {
    if (report && dashboards) {
      const filteredReports = dashboards
        .filter((dashboard) => dashboard.fqn !== report.fullyQualifiedName)
        .map((dashboard) => dashboard.fqn);

      try {
        const resp = await fetch(`${VITE_CATALOG_API}/datasets/${datasetId}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            reports: filteredReports,
          }),
        });

        if (!resp.ok) {
          throw Error("There was a problem trying to remove the report");
        }

        await mutateDataset();
        await mutateDashboards();
        handleClose();
      } catch (e: any) {
        console.error(e);
        rumClient?.recordError(e);
        showError(e.message);
      }
    }
  };

  const messageHandler = async (e: MessageEvent) => {
    if ("workbook:ondelete" === e.data.type) {
      await handleRemove();
    }
  };

  useEffect(() => {
    trackEvent("dataset_dashboard_viewed", {
      dataset_id: datasetId,
      dashboard_id: reportId,
    });
  }, [datasetId, reportId]);

  if (!report) {
    return <Loading />;
  }

  return (
    <SigmaEmbed
      title={report.displayName}
      datasetId={datasetId}
      reportFqn={report.fullyQualifiedName}
      messageHandler={messageHandler}
      onClose={handleClose}
      allowEdits={hasEditPermission}
    />
  );
};

export default DatasetReport;
